import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useCallback,
} from 'react';
import { useAcademicsClassesHook } from '../hooks/useAcademicsClassesHook';
import { useAcademicsCoursesHook } from '../hooks/useAcademicsCoursesHook';
import {
  IAcademicsClassResponse,
  IAcademicsCourseResponse,
} from '../../../../apis/academics/academics.interfaces';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import {
  IAcademicsClasses,
  IAcademicsCourses,
  EStandardLevel,
  EAcademicStream,
} from '../../../../apis/academics/academicsClassesAndCourses.interfaces';
import { useAcademicsFiltersContext } from './academicsFilters.context';

type TQueryError = IFHError<{ message: string }>;
type TAcademicsMode = 'add' | 'edit' | 'details' | null;

type TAcademicsClassesAndCoursesAppState = {
  errorMessage?: string;
  isLoading: boolean;
  paginationParams: TPaginationParams;
  mode: TAcademicsMode;
  selectedClassId: number | null;
  selectedCourseId: number | null;
  selectedClass: IAcademicsClasses | null;
  selectedCourse: IAcademicsCourses | null;
  classes: IAcademicsClasses[];
  courses: IAcademicsCourses[];
  totalClasses: number;
  totalCourses: number;
};

enum ACTION_TYPE {
  SET_PAGINATION_PARAMS = 'SET_PAGINATION_PARAMS',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_MODE = 'SET_MODE',
  SET_SELECTED_CLASS_ID = 'SET_SELECTED_CLASS_ID',
  SET_SELECTED_COURSE_ID = 'SET_SELECTED_COURSE_ID',
  SET_SELECTED_CLASS = 'SET_SELECTED_CLASS',
  SET_SELECTED_COURSE = 'SET_SELECTED_COURSE',
  SET_CLASSES = 'SET_CLASSES',
  SET_COURSES = 'SET_COURSES',
}

type Action =
  | { type: ACTION_TYPE.SET_PAGINATION_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | { type: ACTION_TYPE.SET_MODE; payload: TAcademicsMode }
  | { type: ACTION_TYPE.SET_SELECTED_CLASS_ID; payload: number | null }
  | { type: ACTION_TYPE.SET_SELECTED_COURSE_ID; payload: number | null }
  | { type: ACTION_TYPE.SET_SELECTED_CLASS; payload: IAcademicsClasses | null }
  | { type: ACTION_TYPE.SET_SELECTED_COURSE; payload: IAcademicsCourses | null }
  | { type: ACTION_TYPE.SET_CLASSES; payload: IAcademicsClassResponse }
  | { type: ACTION_TYPE.SET_COURSES; payload: IAcademicsCourseResponse };

const initialAcademicsClassesAndCoursesState: TAcademicsClassesAndCoursesAppState =
  {
    errorMessage: undefined,
    isLoading: false,
    paginationParams: {
      page: 1,
      limit: 10,
    },
    mode: null,
    selectedClassId: null,
    selectedCourseId: null,
    selectedClass: null,
    selectedCourse: null,
    classes: [],
    courses: [],
    totalClasses: 0,
    totalCourses: 0,
  };

interface TAcademicsClassesAndCoursesState {
  // App state
  academicsClassesAndCoursesAppState: TAcademicsClassesAndCoursesAppState;

  // Refetch functions
  refetchClasses: () => void;
  refetchCourses: () => void;

  // Class utility functions
  getClassById: (id: number) => IAcademicsClasses | undefined;
  getClassesByStandardLevel: (level: EStandardLevel) => IAcademicsClasses[];
  getClassesByStream: (stream: EAcademicStream) => IAcademicsClasses[];

  // Course utility functions
  getCourseById: (id: number) => IAcademicsCourses | undefined;
  getCoursesByClass: (classId: number) => IAcademicsCourses[];
  getElectiveCourses: () => IAcademicsCourses[];

  // Loading and error states
  isLoading: boolean;
  isError: boolean;
  error: TQueryError | null;
}

interface AcademicsClassesAndCoursesProviderProps {
  children: ReactNode;
  paginationParams?: TPaginationParams;
}

const AcademicsClassesAndCoursesContext = createContext<
  TAcademicsClassesAndCoursesState | undefined
>(undefined);
const AcademicsClassesAndCoursesDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const academicsClassesAndCoursesReducer = (
  appState: TAcademicsClassesAndCoursesAppState,
  action: Action
): TAcademicsClassesAndCoursesAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_PAGINATION_PARAMS:
      return {
        ...appState,
        paginationParams: { ...appState.paginationParams, ...action.payload },
      };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...appState, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...appState, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_MODE:
      return { ...appState, mode: action.payload };
    case ACTION_TYPE.SET_SELECTED_CLASS_ID:
      return { ...appState, selectedClassId: action.payload };
    case ACTION_TYPE.SET_SELECTED_COURSE_ID:
      return { ...appState, selectedCourseId: action.payload };
    case ACTION_TYPE.SET_SELECTED_CLASS:
      return { ...appState, selectedClass: action.payload };
    case ACTION_TYPE.SET_SELECTED_COURSE:
      return { ...appState, selectedCourse: action.payload };
    case ACTION_TYPE.SET_CLASSES:
      return {
        ...appState,
        classes: action.payload.classes,
        totalClasses: action.payload.total,
      };
    case ACTION_TYPE.SET_COURSES:
      return {
        ...appState,
        courses: action.payload.courses,
        totalCourses: action.payload.total,
      };
    default:
      throw new Error('Unhandled action type');
  }
};

export const AcademicsClassesAndCoursesProvider: FC<
  AcademicsClassesAndCoursesProviderProps
> = ({ children, paginationParams = {} as TPaginationParams }) => {
  const [academicsClassesAndCoursesAppState, dispatch] = useReducer(
    academicsClassesAndCoursesReducer,
    {
      ...initialAcademicsClassesAndCoursesState,
      paginationParams: {
        ...initialAcademicsClassesAndCoursesState.paginationParams,
        ...paginationParams,
      },
    }
  );

  const { academicYear, grade } = useAcademicsFiltersContext();

  const classesQuery = useAcademicsClassesHook({
    ...academicsClassesAndCoursesAppState.paginationParams,
    academicYear,
    grade,
  });
  const coursesQuery = useAcademicsCoursesHook({
    ...academicsClassesAndCoursesAppState.paginationParams,
    academicYear,
  });

  const refetchClasses = useCallback(() => {
    classesQuery.refetch();
  }, [classesQuery]);

  const refetchCourses = useCallback(() => {
    coursesQuery.refetch();
  }, [coursesQuery]);

  // Classes utility functions
  const getClassById = useCallback(
    (id: number): IAcademicsClasses | undefined => {
      return academicsClassesAndCoursesAppState.classes.find(
        (cls) => cls.id === id
      );
    },
    [academicsClassesAndCoursesAppState.classes]
  );

  const getClassesByStandardLevel = useCallback(
    (level: EStandardLevel): IAcademicsClasses[] => {
      return academicsClassesAndCoursesAppState.classes.filter(
        (cls) => cls.standardLevel === level
      );
    },
    [academicsClassesAndCoursesAppState.classes]
  );

  const getClassesByStream = useCallback(
    (stream: EAcademicStream): IAcademicsClasses[] => {
      return academicsClassesAndCoursesAppState.classes.filter(
        (cls) => cls.stream === stream
      );
    },
    [academicsClassesAndCoursesAppState.classes]
  );

  // Course utility functions
  const getCourseById = useCallback(
    (id: number): IAcademicsCourses | undefined => {
      return academicsClassesAndCoursesAppState.courses.find(
        (course) => course.id === id
      );
    },
    [academicsClassesAndCoursesAppState.courses]
  );

  const getCoursesByClass = useCallback(
    (classId: number): IAcademicsCourses[] => {
      return academicsClassesAndCoursesAppState.courses.filter((course) =>
        course.classIds.includes(classId)
      );
    },
    [academicsClassesAndCoursesAppState.courses]
  );

  const getElectiveCourses = useCallback((): IAcademicsCourses[] => {
    return academicsClassesAndCoursesAppState.courses.filter(
      (course) => course.isElective
    );
  }, [academicsClassesAndCoursesAppState.courses]);

  // Loading and error states
  const isLoading =
    academicsClassesAndCoursesAppState.isLoading ||
    classesQuery.isLoading ||
    coursesQuery.isLoading;
  const isError = classesQuery.isError || coursesQuery.isError;
  const error = classesQuery.error || coursesQuery.error || null;

  useEffect(() => {
    const loading = classesQuery.isLoading || coursesQuery.isLoading;
    dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: loading });
  }, [classesQuery.isLoading, coursesQuery.isLoading]);

  useEffect(() => {
    if (classesQuery.data) {
      dispatch({ type: ACTION_TYPE.SET_CLASSES, payload: classesQuery.data });
    }
  }, [classesQuery.data]);

  useEffect(() => {
    if (coursesQuery.data) {
      dispatch({ type: ACTION_TYPE.SET_COURSES, payload: coursesQuery.data });
    }
  }, [coursesQuery.data]);

  useEffect(() => {
    if (isError) {
      const errorMessage =
        classesQuery.error?.response?.data?.message ||
        coursesQuery.error?.response?.data?.message ||
        'Failed to fetch academics data';
      dispatch({ type: ACTION_TYPE.FETCH_DATA_FAILURE, payload: errorMessage });
    }
  }, [isError, classesQuery.error, coursesQuery.error]);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_CLASS_ID,
      payload: null,
    });
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_COURSE_ID,
      payload: null,
    });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_CLASS, payload: null });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_COURSE, payload: null });
    dispatch({ type: ACTION_TYPE.SET_MODE, payload: null });
  }, [academicsClassesAndCoursesAppState.paginationParams]);

  const value = useMemo(
    () => ({
      // App state
      academicsClassesAndCoursesAppState,

      // Refetch functions
      refetchClasses,
      refetchCourses,

      // Class utility functions
      getClassById,
      getClassesByStandardLevel,
      getClassesByStream,

      // Course utility functions
      getCourseById,
      getCoursesByClass,
      getElectiveCourses,

      // Loading and error states
      isLoading,
      isError,
      error,
    }),
    [
      academicsClassesAndCoursesAppState,
      refetchClasses,
      refetchCourses,
      getClassById,
      getClassesByStandardLevel,
      getClassesByStream,
      getCourseById,
      getCoursesByClass,
      getElectiveCourses,
      isLoading,
      isError,
      error,
    ]
  );

  return (
    <AcademicsClassesAndCoursesContext.Provider value={value}>
      <AcademicsClassesAndCoursesDispatchContext.Provider value={dispatch}>
        {children}
      </AcademicsClassesAndCoursesDispatchContext.Provider>
    </AcademicsClassesAndCoursesContext.Provider>
  );
};

export const useAcademicsClassesAndCoursesState =
  (): TAcademicsClassesAndCoursesState => {
    const context = useContext(AcademicsClassesAndCoursesContext);

    if (context === undefined) {
      throw new Error(
        'useAcademicsClassesAndCoursesState must be used within a AcademicsClassesAndCoursesProvider'
      );
    }

    return context;
  };

export const useAcademicsClassesAndCoursesActions = () => {
  const dispatch = useContext(AcademicsClassesAndCoursesDispatchContext);

  if (dispatch === undefined) {
    throw new Error(
      'useAcademicsClassesAndCoursesActions must be used within a AcademicsClassesAndCoursesProvider'
    );
  }

  const updatePaginationParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_PAGINATION_PARAMS, payload: params });
  };

  const setMode = (mode: TAcademicsMode) => {
    dispatch({ type: ACTION_TYPE.SET_MODE, payload: mode });
  };

  const setSelectedClassId = (id: number | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_CLASS_ID, payload: id });
  };

  const setSelectedCourseId = (id: number | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_COURSE_ID, payload: id });
  };

  const setSelectedClass = (academicClass: IAcademicsClasses | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_CLASS, payload: academicClass });
  };

  const setSelectedCourse = (course: IAcademicsCourses | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_COURSE, payload: course });
  };

  const openAddMode = () => {
    dispatch({ type: ACTION_TYPE.SET_MODE, payload: 'add' });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_CLASS, payload: null });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_COURSE, payload: null });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_CLASS_ID, payload: null });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_COURSE_ID, payload: null });
  };

  const openEditMode = (
    item: IAcademicsClasses | IAcademicsCourses,
    type: 'class' | 'course'
  ) => {
    dispatch({ type: ACTION_TYPE.SET_MODE, payload: 'edit' });
    if (type === 'class') {
      const academicClass = item as IAcademicsClasses;
      dispatch({
        type: ACTION_TYPE.SET_SELECTED_CLASS,
        payload: academicClass,
      });
      dispatch({
        type: ACTION_TYPE.SET_SELECTED_CLASS_ID,
        payload: academicClass.id,
      });
      dispatch({ type: ACTION_TYPE.SET_SELECTED_COURSE, payload: null });
      dispatch({ type: ACTION_TYPE.SET_SELECTED_COURSE_ID, payload: null });
    } else {
      const course = item as IAcademicsCourses;
      dispatch({ type: ACTION_TYPE.SET_SELECTED_COURSE, payload: course });
      dispatch({
        type: ACTION_TYPE.SET_SELECTED_COURSE_ID,
        payload: course.id,
      });
      dispatch({ type: ACTION_TYPE.SET_SELECTED_CLASS, payload: null });
      dispatch({ type: ACTION_TYPE.SET_SELECTED_CLASS_ID, payload: null });
    }
  };

  const openDetailsMode = (
    item: IAcademicsClasses | IAcademicsCourses,
    type: 'class' | 'course'
  ) => {
    dispatch({ type: ACTION_TYPE.SET_MODE, payload: 'details' });
    if (type === 'class') {
      const academicClass = item as IAcademicsClasses;
      dispatch({
        type: ACTION_TYPE.SET_SELECTED_CLASS,
        payload: academicClass,
      });
      dispatch({
        type: ACTION_TYPE.SET_SELECTED_CLASS_ID,
        payload: academicClass.id,
      });
      dispatch({ type: ACTION_TYPE.SET_SELECTED_COURSE, payload: null });
      dispatch({ type: ACTION_TYPE.SET_SELECTED_COURSE_ID, payload: null });
    } else {
      const course = item as IAcademicsCourses;
      dispatch({ type: ACTION_TYPE.SET_SELECTED_COURSE, payload: course });
      dispatch({
        type: ACTION_TYPE.SET_SELECTED_COURSE_ID,
        payload: course.id,
      });
      dispatch({ type: ACTION_TYPE.SET_SELECTED_CLASS, payload: null });
      dispatch({ type: ACTION_TYPE.SET_SELECTED_CLASS_ID, payload: null });
    }
  };

  const closeMode = () => {
    dispatch({ type: ACTION_TYPE.SET_MODE, payload: null });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_CLASS, payload: null });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_COURSE, payload: null });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_CLASS_ID, payload: null });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_COURSE_ID, payload: null });
  };

  return {
    updatePaginationParams,
    setMode,
    setSelectedClassId,
    setSelectedCourseId,
    setSelectedClass,
    setSelectedCourse,
    openAddMode,
    openEditMode,
    openDetailsMode,
    closeMode,
  };
};
